// features/forms/page/form-upsert/form-upsert.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { TranslatePipe } from '@ngx-translate/core';

import { AppRoutes } from '@core/constants/app.routes';
import { Form, FormCreate, FormUpdate } from '@core/entities/models/form.model';
import { FormService } from '@core/services/use-cases/form.service';
import { SnackbarService } from '@shared/services/snackbar.service';

import { UiMoleculeInputComponent } from '@shared/components/molecules/ui-molecule-input/ui-molecule-input.component';
import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';

@Component({
  selector: 'app-form-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiOrganismMainNavComponent,
  ],
  templateUrl: './form-upsert.html',
  styleUrl: './form-upsert.scss',
})
export class FormUpsert {
  // ---------------------------------------------------------------------------
  // INYECCIONES
  // ---------------------------------------------------------------------------

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly formService = inject(FormService);
  private readonly snackbar = inject(SnackbarService);

  // ---------------------------------------------------------------------------
  // PROPIEDADES
  // ---------------------------------------------------------------------------

  /** IDs del contexto actual */
  private countryId: string | null = null;
  private partnerId: string | null = null;

  protected readonly AppRoutes = AppRoutes;

  // ---------------------------------------------------------------------------
  // FORMULARIO
  // ---------------------------------------------------------------------------

  protected readonly form: FormGroup;
  protected editingId: string | null = null;
  protected currentForm: Form | null = null;

  constructor() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(150)]],
    });

    // Obtener IDs del contexto de la ruta
    this.extractContextFromRoute();

    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.loadForEdit(idFromRoute);
    }
  }

  /**
   * Extrae los IDs de country y partner de la ruta actual
   */
  private extractContextFromRoute(): void {
    const urlSegments = this.router.url.split('/').filter((segment) => segment);

    // Buscar countryId y partnerId en la URL
    const countryIndex = urlSegments.indexOf('countries');
    const partnerIndex = urlSegments.indexOf('partners');

    if (countryIndex !== -1 && urlSegments[countryIndex + 1]) {
      this.countryId = urlSegments[countryIndex + 1];
    }

    if (partnerIndex !== -1 && urlSegments[partnerIndex + 1]) {
      this.partnerId = urlSegments[partnerIndex + 1];
    }
  }

  // ---------------------------------------------------------------------------
  // GETTERS
  // ---------------------------------------------------------------------------

  protected get nameControl() {
    return this.form.get('name');
  }

  // ---------------------------------------------------------------------------
  // CARGA EN MODO EDICIÓN
  // ---------------------------------------------------------------------------

  private loadForEdit(id: string): void {
    // Determinar qué endpoint usar según el contexto
    let formObservable;

    if (this.partnerId) {
      // Contexto de partner: usar endpoint de partner
      formObservable = this.formService.getByIdInPartner(this.partnerId, id);
    } else if (this.countryId) {
      // Contexto de country: usar endpoint de country
      formObservable = this.formService.getByIdInCountry(this.countryId, id);
    } else {
      // Sin contexto: usar endpoint general
      formObservable = this.formService.getById(id);
    }

    formObservable.subscribe({
      next: (form) => {
        this.editingId = form.id;
        this.currentForm = form;

        this.form.patchValue({
          name: form.name,
        });
      },
      error: (error) => {
        this.snackbar.notifyHttpError(error);
        this.backToList();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // SUBMIT
  // ---------------------------------------------------------------------------

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    if (this.editingId) {
      // Modo edición
      const payload: FormUpdate = {
        name: raw.name,
      };

      this.formService.update(this.editingId, payload).subscribe({
        next: () => {
          this.backToList();
        },
        error: (error) => this.snackbar.notifyHttpError(error),
      });
    } else {
      // Modo creación
      const payload: FormCreate = {
        name: raw.name,
        schema: {}, // Schema vacío por defecto
      };

      this.formService.create(payload).subscribe({
        next: () => {
          this.backToList();
        },
        error: (error) => this.snackbar.notifyHttpError(error),
      });
    }
  }

  protected cancel(): void {
    this.backToList();
  }

  private backToList(): void {
    // Navegar hacia atrás a la página anterior
    this.location.back();
  }
}
